import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { apiService } from '../../services/api';
import { VehicleDetailCopy } from '../../pages/public/types';
import { digitsOnly, formatUsPhone } from './ContactLeadModal';

type VehicleContactFormValues = {
  fullName: string;
  phone: string;
  email?: string;
  message?: string;
};

interface VehicleContactFormProps {
  vehicleId: string;
  vehicleLabel: string;
  dealerPhone: string;
  labels: VehicleDetailCopy;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildSchema = (labels: VehicleDetailCopy) =>
  z.object({
    fullName: z.string().trim().min(2, labels.contactNameRequired),
    phone: z.string().refine((v) => digitsOnly(v).length === 10, labels.contactPhoneInvalid),
    email: z
      .string()
      .trim()
      .refine((v) => !v || emailPattern.test(v), labels.contactEmailInvalid)
      .optional(),
    message: z.string().max(2000, labels.contactMessageTooLong).optional(),
  });

const VehicleContactForm: React.FC<VehicleContactFormProps> = ({
  vehicleId,
  vehicleLabel,
  dealerPhone,
  labels,
}) => {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const schema = buildSchema(labels);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<VehicleContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: '',
      phone: '',
      email: '',
      message: labels.contactMessageDefault(vehicleLabel),
    },
  });

  const phoneValue = watch('phone') || '';

  useEffect(() => {
    if (!dirtyFields.message) {
      setValue('message', labels.contactMessageDefault(vehicleLabel));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, vehicleLabel]);

  if (status === 'success') {
    return (
      <div
        role="status"
        className="rounded-xl border border-green-200 bg-green-50 p-5 text-sm font-semibold text-green-800"
      >
        {labels.contactSuccess}
      </div>
    );
  }

  const submit = async (values: VehicleContactFormValues) => {
    try {
      await apiService.post('/website/leads/public', {
        full_name: values.fullName.trim(),
        phone: values.phone.trim(),
        email: values.email?.trim() || undefined,
        channel: 'website',
        vehicle_id: vehicleId,
        message: values.message?.trim() || null,
      });
      setStatus('success');
    } catch (error) {
      console.warn('VDP contact form submission failed.', error);
      setStatus('error');
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 md:p-6">
      <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-gray-500">
        {labels.contactFormTitle}
      </h2>
      <form className="space-y-4" onSubmit={handleSubmit(submit)}>
        <div>
          <label
            className="block text-sm font-semibold text-gray-700"
            htmlFor="vehicle-contact-name"
          >
            {labels.contactName}
          </label>
          <input
            id="vehicle-contact-name"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
            autoComplete="name"
            {...register('fullName')}
          />
          {errors.fullName && (
            <p role="alert" className="mt-1 text-sm text-red-600">
              {errors.fullName.message}
            </p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-gray-700"
            htmlFor="vehicle-contact-phone"
          >
            {labels.contactPhoneLabel}
          </label>
          <input
            id="vehicle-contact-phone"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
            inputMode="tel"
            autoComplete="tel"
            value={phoneValue}
            onChange={(event) =>
              setValue('phone', formatUsPhone(event.target.value), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
          {errors.phone && (
            <p role="alert" className="mt-1 text-sm text-red-600">
              {errors.phone.message}
            </p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-gray-700"
            htmlFor="vehicle-contact-email"
          >
            {labels.contactEmailLabel}
          </label>
          <input
            id="vehicle-contact-email"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
            type="email"
            autoComplete="email"
            {...register('email')}
          />
          {errors.email && (
            <p role="alert" className="mt-1 text-sm text-red-600">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-gray-700"
            htmlFor="vehicle-contact-message"
          >
            {labels.contactMessage}
          </label>
          <textarea
            id="vehicle-contact-message"
            className="mt-1 min-h-24 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
            {...register('message')}
          />
          {errors.message && (
            <p role="alert" className="mt-1 text-sm text-red-600">
              {errors.message.message}
            </p>
          )}
        </div>

        {status === 'error' && (
          <p role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {labels.contactError(dealerPhone)}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {isSubmitting ? labels.contactSubmitting : labels.contactSubmit}
        </button>
      </form>
    </div>
  );
};

export default VehicleContactForm;
